import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router";
import { Pill } from "@truevault/aquilo";
import { Routes } from "../../root/routes";
import type { RequestWorkflowTab } from "./WorkflowSetupTabs";
import { WorkflowType } from "../../../common/service/server/dto/RegulationRequestTypeDto";
import { usePrimaryOrganization } from "../../hooks";
import { useDataMap } from "../../../common/hooks/api/useDataMap";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import {
  BUSINESS_SURVEY_NAME,
  KIDS_UNDER_16_DATA_COLLECTION_QUESTION_SLUG,
} from "../../surveys/businessSurvey";
import { getSensitivePICsWithFilters } from "../../../common/service/resources/data-map/picHelpers";

export type WorkflowNavItem = {
  tabKey: RequestWorkflowTab;
  name: string;
  route: string;
  /** Whether this workflow is applicable to the organization */
  isApplicable: boolean;
  /** Whether to show "Set Up" badge - during onboarding or after changes */
  showSetUpBadge?: boolean;
};

/** Maps workflow tabs to their WorkflowType for applicability checks */
const TAB_TO_WORKFLOW_TYPE: Partial<Record<RequestWorkflowTab, WorkflowType>> = {
  "request-to-delete": WorkflowType.DELETE,
  "correction-request": WorkflowType.CORRECTION,
  "access-request": WorkflowType.KNOW,
  "appeal-request": WorkflowType.APPEAL,
  "withdrawal-of-consent-request": WorkflowType.WITHDRAW_CONSENT,
  "profiling-information-request": WorkflowType.PROFILING_INFORMATION,
  "portability-request": WorkflowType.PORTABILITY,
  "objection-request": WorkflowType.OBJECTION,
  "restrict-processing-request": WorkflowType.RESTRICT_PROCESSING,
  "request-to-opt-out": WorkflowType.OPT_OUT,
  "request-to-limit": WorkflowType.LIMIT,
  "request-to-profiling-opt-out": WorkflowType.PROFILING_OPT_OUT,
};

type WorkflowSetupVerticalNavProps = {
  selectedTabKey: RequestWorkflowTab;
  /** Returns true if org has request types for this workflow - used to show only relevant workflows */
  hasRequestTypesForWorkflow: (workflowType: WorkflowType) => boolean;
  isLimitStepIncomplete: boolean;
  isFinishedWorkflowSetupOnboarding: boolean;
};

const WORKFLOW_ITEMS: Omit<WorkflowNavItem, "isApplicable" | "showSetUpBadge">[] = [
  {
    tabKey: "consumer-lookup",
    name: "Consumer Lookup",
    route: Routes.organization.requestHandling.consumerGroup,
  },
  {
    tabKey: "request-to-delete",
    name: "Deletion",
    route: Routes.organization.requestHandling.requestToDelete,
  },
  {
    tabKey: "access-request",
    name: "Access",
    route: Routes.organization.requestHandling.accessRequest,
  },
  {
    tabKey: "correction-request",
    name: "Correction",
    route: Routes.organization.requestHandling.requestToCorrection,
  },
  {
    tabKey: "appeal-request",
    name: "Appeal",
    route: Routes.organization.requestHandling.requestToAppeal,
  },
  {
    tabKey: "withdrawal-of-consent-request",
    name: "Withdrawal of Consent",
    route: Routes.organization.requestHandling.requestToWithdrawalOfConsent,
  },
  {
    tabKey: "profiling-information-request",
    name: "Profiling Information",
    route: Routes.organization.requestHandling.requestToProfilingInformation,
  },
  {
    tabKey: "portability-request",
    name: "Portability",
    route: Routes.organization.requestHandling.requestToPortability,
  },
  {
    tabKey: "objection-request",
    name: "Objection",
    route: Routes.organization.requestHandling.requestToObjection,
  },
  {
    tabKey: "restrict-processing-request",
    name: "Restrict Processing",
    route: Routes.organization.requestHandling.requestToRestrictProcessing,
  },
  {
    tabKey: "request-to-opt-out",
    name: "Opt-Out",
    route: Routes.organization.requestHandling.requestToOptOut,
  },
  {
    tabKey: "request-to-limit",
    name: "Limit",
    route: Routes.organization.requestHandling.requestToLimit,
  },
  {
    tabKey: "request-to-profiling-opt-out",
    name: "Profiling Opt-Out",
    route: Routes.organization.requestHandling.requestToProfilingOptOut,
  },
];

export function WorkflowSetupVerticalNav({
  selectedTabKey,
  hasRequestTypesForWorkflow,
  isLimitStepIncomplete,
  isFinishedWorkflowSetupOnboarding,
}: WorkflowSetupVerticalNavProps) {
  const history = useHistory();
  const location = useLocation();
  const navItems = useWorkflowSetupNavItems({
    selectedTabKey,
    hasRequestTypesForWorkflow,
    isLimitStepIncomplete,
    isFinishedWorkflowSetupOnboarding,
  });

  return (
    <nav className="w-56 flex-shrink-0" aria-label="Workflow setup">
      <ul className="space-y-1">
        {navItems.map((item) => {
          const isSelected = selectedTabKey === item.tabKey || location.pathname === item.route;
          return (
            <React.Fragment key={item.tabKey}>
              <li>
                <button
                  type="button"
                  onClick={() => history.push(item.route)}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 text-left rounded-lg text-sm font-medium
                    transition-colors
                    ${
                      isSelected
                        ? "bg-primary-50 text-primary-700"
                        : "text-neutral-700 hover:bg-neutral-100"
                    }
                  `}
                >
                  <span>{item.name}</span>
                  {item.showSetUpBadge && (
                    <div className="pl-2">
                      <Pill variant="warning" noMargin>
                        Set Up
                      </Pill>
                    </div>
                  )}
                </button>
              </li>
              {item.tabKey === "consumer-lookup" && navItems.length > 1 && (
                <li aria-hidden className="my-2 border-t border-neutral-200" />
              )}
            </React.Fragment>
          );
        })}
      </ul>
    </nav>
  );
}

export function useWorkflowSetupNavItems({
  hasRequestTypesForWorkflow,
  isLimitStepIncomplete,
  isFinishedWorkflowSetupOnboarding,
}: WorkflowSetupVerticalNavProps): WorkflowNavItem[] {
  const [organization] = usePrimaryOrganization();
  const { data: dataMap } = useDataMap(organization?.id);
  const { answers: bizSurveyAnswers } = useSurvey(organization?.id, BUSINESS_SURVEY_NAME);

  const hasSensitivePicDisclosuresForLimit = (() => {
    if (!dataMap) {
      return false;
    }

    const sensitivePicIds = new Set(
      getSensitivePICsWithFilters(dataMap, {
        includeInferencesBasedOnSensitiveInformationPIC: true,
      }).map((pic) => pic.id),
    );

    return dataMap.disclosure.some((disclosure) => {
      const collectionGroup = dataMap.collectionGroups[disclosure.groupId];
      if (!collectionGroup || collectionGroup.type === "EMPLOYMENT") {
        return false;
      }
      return sensitivePicIds.has(disclosure.picId);
    });
  })();

  const childrenUnder16DataCollectionEnabled =
    bizSurveyAnswers?.[KIDS_UNDER_16_DATA_COLLECTION_QUESTION_SLUG] === "true";
  const isLimitApplicable =
    hasSensitivePicDisclosuresForLimit || childrenUnder16DataCollectionEnabled;

  return useMemo(() => {
    return WORKFLOW_ITEMS.map((item) => {
      const workflowType = TAB_TO_WORKFLOW_TYPE[item.tabKey];
      const isLimit = item.tabKey === "request-to-limit";
      const isProfilingOptOut = item.tabKey === "request-to-profiling-opt-out";

      // Consumer Lookup has no workflow type - always applicable
      // Other workflows: show only when org has request types for that workflow
      const hasWorkflowRequestTypes = !workflowType || hasRequestTypesForWorkflow(workflowType);
      const isApplicable = (() => {
        if (!hasWorkflowRequestTypes) {
          return false;
        }
        if (isLimit) {
          return Boolean(isLimitApplicable);
        }
        if (isProfilingOptOut) {
          return Boolean(organization?.requiresProfilingOptOut);
        }
        return true;
      })();

      const showSetUpBadge = isFinishedWorkflowSetupOnboarding && isLimit && isLimitStepIncomplete;

      return {
        ...item,
        isApplicable,
        showSetUpBadge,
      };
    }).filter((item) => item.isApplicable);
  }, [
    hasRequestTypesForWorkflow,
    isFinishedWorkflowSetupOnboarding,
    isLimitApplicable,
    isLimitStepIncomplete,
    organization?.requiresProfilingOptOut,
  ]);
}
